import { compareHuge, huge, type HugeNumber } from "@/shared/numbers";
import { calculateNHovExperienceForLevel } from "./nHovGrowthLogic";

type ResolveEffectAtLevel = (level: number) => HugeNumber;

export function interpolateNHovEffectAtContinuousLevel(options: {
  readonly level: number;
  readonly additiveIdentityOffset?: number;
  readonly resolveEffectAtIntegerLevel: ResolveEffectAtLevel;
}): HugeNumber {
  const level = options.level;
  if (!Number.isFinite(level) || level <= 0) {
    return options.resolveEffectAtIntegerLevel(0);
  }
  const lowerLevel = Math.floor(level);
  const fraction = level - lowerLevel;
  if (fraction <= 0 || lowerLevel + 1 === lowerLevel) {
    return options.resolveEffectAtIntegerLevel(lowerLevel);
  }

  const offset = huge(options.additiveIdentityOffset ?? 0);
  const lower = options.resolveEffectAtIntegerLevel(lowerLevel).add(offset);
  const upper = options.resolveEffectAtIntegerLevel(lowerLevel + 1).add(offset);
  if (compareHuge(lower, 0) <= 0 || compareHuge(upper, lower) <= 0) {
    return lower.sub(offset);
  }
  return lower.mul(upper.div(lower).pow(fraction)).sub(offset);
}

export function resolveNHovExperienceForEffect(options: {
  readonly currentContinuousLevel: number;
  readonly freshNextContinuousLevel: number;
  readonly targetEffect: HugeNumber;
  readonly additiveIdentityOffset?: number;
  readonly resolveEffectAtLevel: ResolveEffectAtLevel;
}): HugeNumber {
  const currentLevel = normalizeLevel(options.currentContinuousLevel);
  const freshNextLevel = Math.max(
    currentLevel,
    normalizeLevel(options.freshNextContinuousLevel),
  );
  const currentEffect = options.resolveEffectAtLevel(currentLevel);
  if (compareHuge(options.targetEffect, currentEffect) <= 0) {
    return calculateNHovExperienceForLevel(currentLevel);
  }
  const freshEffect = options.resolveEffectAtLevel(freshNextLevel);
  if (compareHuge(options.targetEffect, freshEffect) >= 0) {
    return calculateNHovExperienceForLevel(freshNextLevel);
  }

  let lowerLevel = Math.floor(currentLevel);
  let upperLevel = Math.ceil(freshNextLevel);
  while (upperLevel - lowerLevel > 1) {
    const midpoint = Math.floor(lowerLevel + (upperLevel - lowerLevel) / 2);
    if (midpoint <= lowerLevel || midpoint >= upperLevel) break;
    const midpointEffect = options.resolveEffectAtLevel(midpoint);
    if (compareHuge(midpointEffect, options.targetEffect) <= 0) {
      lowerLevel = midpoint;
    } else {
      upperLevel = midpoint;
    }
  }

  const offset = huge(options.additiveIdentityOffset ?? 0);
  const lower = options.resolveEffectAtLevel(lowerLevel).add(offset);
  const upper = options.resolveEffectAtLevel(upperLevel).add(offset);
  const target = options.targetEffect.add(offset);
  if (
    compareHuge(lower, 0) <= 0 ||
    compareHuge(upper, lower) <= 0 ||
    compareHuge(target, lower) <= 0
  ) {
    return calculateNHovExperienceForLevel(lowerLevel);
  }

  const logRange = upper.log10().sub(lower.log10());
  if (compareHuge(logRange, 0) <= 0) {
    return calculateNHovExperienceForLevel(lowerLevel);
  }
  const fraction = target.log10().sub(lower.log10()).div(logRange).toNumber();
  const continuousLevel = lowerLevel + clamp01(fraction);
  return calculateNHovExperienceForLevel(continuousLevel);
}

function normalizeLevel(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}
